import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { ArrowLeft, PackageCheck, Truck, MapPin, User, CreditCard, Package, RefreshCcw } from "lucide-react";

import Button from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import sellersApi from "@/services/api/sellers";
import type { Order, Address } from "@/services/types/domain";
import { formatPrice, tOrderStatus, tPaymentStatus, orderVariant, paymentVariant } from "@/utils/formatters";
// ==============================
// Tipos locales (detalle seller)
// ==============================
export interface SellerOrderItem {
    id: number;
    order_id?: number;
    product_id: number;
    variant_id?: number | null;
    seller_id?: number;
    product_name: string;
    product_sku?: string;
    quantity: number;
    price: number | string; // puede venir string
    total: number | string; // puede venir string
    product_slug?: string;
    store_name?: string;
    image_url?: string | null;
}

export interface SellerTrackingEvent {
    status: string; // e.g. created, processing, shipped, delivered, cancelled
    note?: string | null;
    created_at: string; // fecha ISO o "YYYY-MM-DD HH:mm:ss"
}

export type SellerOrderDetail = Order & {
    seller_items: SellerOrderItem[];
    seller_revenue_in_order?: number; // ingreso de esta tienda en la orden
    seller_items_in_order?: number;
    tracking?: SellerTrackingEvent[];
    shipping_address: Address;
    billing_address: Address;
};

// ==============================
// Utils locales
// ==============================
const toNum = (v: any, d = 0) => {
    if (v === null || v === undefined || v === "") return d;
    const n = typeof v === "string" ? parseFloat(v) : Number(v);
    return Number.isFinite(n) ? n : d;
};


// ==============================
// Componente principal
// ==============================
const SellerOrderDetailPage: React.FC = () => {
    const { id } = useParams();

    const [order, setOrder] = useState<SellerOrderDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Campos controlados para acciones (solo si el servicio los soporta)
    const [trackingNumber, setTrackingNumber] = useState("");
    const [carrier, setCarrier] = useState("");
    const actionBusy = useRef(false);

    const currency = order?.currency || "PEN";

    // Cargar detalle
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                setLoading(true);
                setError(null);
                // El servicio debe adaptar ApiResponse → objeto limpio
                const data = await (sellersApi as any).getSellerOrderById?.(Number(id));
                const payload: SellerOrderDetail = data?.data ?? data; // tolerante a ApiResponse
                if (!mounted) return;
                setOrder(payload);
            } catch (err: any) {
                const msg = err?.response?.data?.message || err?.message || "No se pudo obtener el pedido";
                setError(msg);
                toast.error(msg);
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [id]);

    const totals = useMemo(() => {
        if (!order) return { subtotal: 0, tax: 0, shipping: 0, discount: 0, total: 0 };
        return {
            subtotal: toNum(order.subtotal),
            tax: toNum(order.tax_amount),
            shipping: toNum(order.shipping_amount),
            discount: toNum(order.discount_amount),
            total: toNum(order.total_amount),
        };
    }, [order]);

    const shippingAddr = order?.shipping_address as Address | undefined;
    //const billingAddr = order?.billing_address as Address | undefined;

    // Acciones condicionales (solo se muestran si existen en el servicio)
    const canMarkShipped = typeof (sellersApi as any).markSellerOrderShipped === "function";
    const canUpdateTracking = typeof (sellersApi as any).updateSellerOrderTracking === "function";

    const handleMarkShipped = async () => {
        if (!order || !canMarkShipped || actionBusy.current) return;
        try {
            actionBusy.current = true;
            await (sellersApi as any).markSellerOrderShipped(order.id);
            toast.success("Pedido marcado como enviado");
            // refresca
            const data = await (sellersApi as any).getSellerOrderById(order.id);
            setOrder(data?.data ?? data);
        } catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || "No se pudo actualizar el estado");
        } finally {
            actionBusy.current = false;
        }
    };

    const handleUpdateTracking = async () => {
        if (!order || !canUpdateTracking || actionBusy.current) return;
        if (!trackingNumber) {
            toast.error("Ingresa el número de seguimiento");
            return;
        }
        try {
            actionBusy.current = true;
            await (sellersApi as any).updateSellerOrderTracking(order.id, { tracking_number: trackingNumber, carrier });
            toast.success("Tracking actualizado");
            const data = await (sellersApi as any).getSellerOrderById(order.id);
            setOrder(data?.data ?? data);
            setTrackingNumber("");
            setCarrier("");
        } catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || "No se pudo actualizar el tracking");
        } finally {
            actionBusy.current = false;
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen grid place-items-center bg-gray-50">
                <div className="text-gray-500 animate-pulse">Cargando pedido…</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 px-4 py-6">
                <div className="mx-auto max-w-5xl">
                    <div className="mb-4"><Link to="/seller/orders" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"><ArrowLeft className="w-4 h-4 mr-1" /> Volver</Link></div>
                    <div className="rounded-2xl border bg-white p-6 text-red-600">{error}</div>
                </div>
            </div>
        );
    }

    if (!order) return null;

    return (
        <div className="min-h-screen bg-gray-50 px-4 py-6">
            <div className="mx-auto max-w-5xl">
                {/* Top bar */}
                <div className="mb-4 flex items-center justify-between">
                    <Link to="/seller/orders" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900">
                        <ArrowLeft className="w-4 h-4 mr-1" /> Volver a pedidos
                    </Link>
                    <Button variant="outline" onClick={async () => {
                        try {
                            const data = await (sellersApi as any).getSellerOrderById(order.id);
                            setOrder(data?.data ?? data);
                            toast.success("Actualizado");
                        } catch (err: any) {
                            toast.error(err?.response?.data?.message || err?.message || "No se pudo refrescar");
                        }
                    }} leftIcon={<RefreshCcw className="w-4 h-4" />}>Refrescar</Button>
                </div>

                {/* Header del pedido */}
                <div className="rounded-2xl border bg-white p-4 sm:p-6 shadow-sm mb-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div>
                            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Pedido {order.order_number || `#${order.id}`}</h1>
                            <div className="text-sm text-gray-500">Creado: {order.created_at ? new Date(order.created_at).toLocaleString() : "—"}</div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div className="flex items-center gap-2 text-sm">
                                <span className="text-gray-500">Pedido</span>
                                <Badge variant={orderVariant(order.status)} icon={<Package className="w-4 h-4" />} iconPosition="left">
                                    {tOrderStatus(order.status)}
                                </Badge>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                                <span className="text-gray-500">Pago</span>
                                <Badge variant={paymentVariant(order.payment_status)} icon={<CreditCard className="w-4 h-4" />} iconPosition="left">
                                    {tPaymentStatus(order.payment_status)}
                                </Badge>
                            </div>
                        </div>

                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Columna principal: items */}
                    <div className="lg:col-span-2 space-y-6">
                        <div className="rounded-2xl border bg-white p-4 sm:p-6 shadow-sm">
                            <div className="mb-3 flex items-center justify-between">
                                <div className="text-lg font-semibold text-gray-900 flex items-center gap-2"><PackageCheck className="w-5 h-5" /> Artículos</div>
                                <div className="text-sm text-gray-600">Ítems de tu tienda: <span className="font-semibold">{order.seller_items_in_order ?? order.seller_items?.length ?? 0}</span></div>
                            </div>

                            <div className="hidden md:grid grid-cols-12 gap-2 text-xs font-semibold text-gray-500 px-2 pb-2">
                                <div className="col-span-6">Producto</div>
                                <div className="col-span-2 text-right">Precio</div>
                                <div className="col-span-2 text-right">Cantidad</div>
                                <div className="col-span-2 text-right">Total</div>
                            </div>

                            <div className="divide-y">
                                {order.seller_items?.map((it) => (
                                    <div key={it.id} className="py-3 px-2 grid grid-cols-1 gap-2 md:grid-cols-12 md:items-center">
                                        <div className="md:col-span-6">
                                            <div className="font-medium text-gray-900">{it.product_name}</div>
                                            {it.product_sku && <div className="text-xs text-gray-500">SKU: {it.product_sku}</div>}
                                            {it.store_name && <div className="text-xs text-gray-500">Tienda: {it.store_name}</div>}
                                        </div>
                                        <div className="md:col-span-2 text-right text-sm text-gray-700">{formatPrice(toNum(it.price), currency)}</div>
                                        <div className="md:col-span-2 text-right text-sm text-gray-700">{it.quantity}</div>
                                        <div className="md:col-span-2 text-right text-sm font-semibold text-gray-900">{formatPrice(toNum(it.total), currency)}</div>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-4 flex flex-col items-end gap-1 text-sm">
                                <div className="flex w-full md:w-auto justify-between md:justify-end gap-6"><span className="text-gray-600">Subtotal</span><span className="font-medium">{formatPrice(totals.subtotal, currency)}</span></div>
                                <div className="flex w-full md:w-auto justify-between md:justify-end gap-6"><span className="text-gray-600">Impuestos</span><span className="font-medium">{formatPrice(totals.tax, currency)}</span></div>
                                <div className="flex w-full md:w-auto justify-between md:justify-end gap-6"><span className="text-gray-600">Envío</span><span className="font-medium">{formatPrice(totals.shipping, currency)}</span></div>
                                <div className="flex w-full md:w-auto justify-between md:justify-end gap-6"><span className="text-gray-600">Descuento</span><span className="font-medium">- {formatPrice(totals.discount, currency)}</span></div>
                                <div className="flex w-full md:w-auto justify-between md:justify-end gap-6 text-base"><span className="text-gray-900 font-semibold">Total</span><span className="font-bold">{formatPrice(totals.total, currency)}</span></div>
                                {typeof order.seller_revenue_in_order !== "undefined" && (
                                    <div className="flex w-full md:w-auto justify-between md:justify-end gap-6 text-sm"><span className="text-gray-600">Ingresos de tu tienda</span><span className="font-semibold">{formatPrice(toNum(order.seller_revenue_in_order), currency)}</span></div>
                                )}
                            </div>
                        </div>

                        {/* Tracking */}
                        {(order.tracking?.length || 0) > 0 && (
                            <div className="rounded-2xl border bg-white p-4 sm:p-6 shadow-sm">
                                <div className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2"><Truck className="w-5 h-5" /> Seguimiento</div>
                                <div className="space-y-3">
                                    {order.tracking!.map((t, idx) => (
                                        <div key={idx} className="flex items-start justify-between gap-3">
                                            <div>
                                                <div className="font-medium text-gray-900">{t.status}</div>
                                                {t.note && <div className="text-sm text-gray-600">{t.note}</div>}
                                            </div>
                                            <div className="text-xs text-gray-500">{new Date(t.created_at).toLocaleString()}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Columna lateral: cliente, direcciones, acciones */}
                    <div className="space-y-6">
                        {/* Cliente */}
                        <div className="rounded-2xl border bg-white p-4 sm:p-6 shadow-sm">
                            <div className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2"><User className="w-5 h-5" /> Cliente</div>
                            <div className="text-sm text-gray-700">
                                <div className="font-medium">{[order.first_name, order.last_name].filter(Boolean).join(" ") || order.email || "—"}</div>
                                {order.email && <div className="text-gray-500">{order.email}</div>}
                            </div>
                        </div>

                        {/* Direcciones */}
                        <div className="rounded-2xl border bg-white p-4 sm:p-6 shadow-sm">
                            <div className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2"><MapPin className="w-5 h-5" /> Envío</div>
                            <div className="text-sm text-gray-700">
                                {shippingAddr ? (
                                    <>
                                        <div className="font-medium">{shippingAddr.recipient_name}</div>
                                        <div>{shippingAddr.address_line_1}{shippingAddr.address_line_2 ? `, ${shippingAddr.address_line_2}` : ""}</div>
                                        <div>{shippingAddr.city}, {shippingAddr.state} {shippingAddr.postal_code}</div>
                                        <div>{shippingAddr.country}</div>
                                        {shippingAddr.phone && <div className="text-gray-500 mt-1">{shippingAddr.phone}</div>}
                                    </>
                                ) : (
                                    <div className="text-gray-500">Sin dirección</div>
                                )}
                            </div>
                        </div>

                        {/* Pago */}
                        <div className="rounded-2xl border bg-white p-4 sm:p-6 shadow-sm">
                            <div className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2"><CreditCard className="w-5 h-5" /> Pago</div>
                            <div className="flex flex-wrap gap-2 items-center text-sm">
                                <span className="text-gray-700">Estado:</span>
                                <Badge variant={paymentVariant(order.payment_status)}>{tPaymentStatus(order.payment_status)}</Badge>
                            </div>
                        </div>

                        {/* Acciones del vendedor */}
                        {(canMarkShipped || canUpdateTracking) && (
                            <div className="rounded-2xl border bg-white p-4 sm:p-6 shadow-sm">
                                <div className="text-lg font-semibold text-gray-900 mb-3">Acciones</div>
                                {canMarkShipped && (
                                    <Button fullWidth className="mb-3" onClick={handleMarkShipped} leftIcon={<PackageCheck className="w-4 h-4" />}>
                                        Marcar como enviado
                                    </Button>
                                )}

                                {canUpdateTracking && (
                                    <div className="space-y-2">
                                        <div>
                                            <label className="text-sm text-gray-700">Número de seguimiento</label>
                                            <input
                                                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                                value={trackingNumber}
                                                onChange={(e) => setTrackingNumber(e.target.value)}
                                                placeholder="Ej. 1Z999AA10123456784"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-sm text-gray-700">Transportista</label>
                                            <input
                                                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                                value={carrier}
                                                onChange={(e) => setCarrier(e.target.value)}
                                                placeholder="Ej. DHL / UPS / Serpost"
                                            />
                                        </div>
                                        <Button fullWidth variant="outline" onClick={handleUpdateTracking} leftIcon={<Truck className="w-4 h-4" />}>
                                            Actualizar tracking
                                        </Button>
                                    </div>
                                )}

                                {!canMarkShipped && !canUpdateTracking && (
                                    <div className="text-sm text-gray-500">Tu servicio de vendedores aún no expone acciones para este pedido.</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SellerOrderDetailPage;
